import React, { useState, useEffect } from "react";
import "./ScheduledContent.css";
import "../../Components/Header/Header.css";
import Header from "./../../Components/Header/Header";
import Scheduled_Content_Search from "./../../assets/images/Scheduled_Content_Search.svg";
import Dropdown_Icon from "./../../assets/images/Dropdown_Icon.svg";
import PaginationLeft from "../../assets/images/pagination-left-arrow.svg";
import PaginationRight from "../../assets/images/pagination-right-arrow.svg";
import { callApi } from "../../utils/apiService";
import { videoCreatorEndPoint } from "../../Constants/constants";
import { formatDuration, formatDurationSeconds } from "../../utils/helpers";
import dayjs from "dayjs";

export const getDay = (date) => dayjs(date).format("DD");
export const getMonth = (date) => dayjs(date).format("MMM");
export const getYear = (date) => dayjs(date).format("YYYY");
export const getTime = (date) => dayjs(date).format("h:mm A");

export default function ScheduledContent() {
  const [scheduledContent, setScheduledContent] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("Type");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const getScheduledContent = async (page = 1, search = "", type = "") => {
    try {
      const params = new URLSearchParams({
        page,
        limit: ITEMS_PER_PAGE,
      });

      if (search) {
        params.append("search", search);
      }

      if (type && type !== "Type") {
        params.append("type", type);
      }

      const response = await callApi(
        `${videoCreatorEndPoint}contentDashboard/scheduleContent?${params.toString()}`,
        "GET",
      );

      if (response?.status) {
        setScheduledContent(response?.data?.content || []);
        setTotalPages(response?.data?.totalPages || 0);
        setTotalItems(response?.data?.totalItems || 0);
        setCurrentPage(response?.data?.page || 1);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getScheduledContent(currentPage, searchTerm, filterType);
  }, [currentPage, searchTerm, filterType]);

  const startIndex =
    totalItems === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;

  const endIndex = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

  const showingFrom = totalItems === 0 ? 0 : startIndex + 1;
  const showingTo = Math.min(endIndex, totalItems);

  const pageNumbers = Array.from(
    { length: totalPages },
    (_, index) => index + 1,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType]);

  const getTypeColor = (type) => {
    switch (type) {
      case "Video":
        return "sc-type-video";
      case "Story":
        return "sc-type-story";
      case "Reel":
        return "sc-type-reel";
      default:
        return "";
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-bg-stripe"></div>
      <Header />
      <div className="sc-container">
        <div className="sc-header">
          <div className="sc-content-wrapper">
            <h1 className="sc-title">Scheduled Content</h1>
            <p className="sc-subtitle">
              This page displays content that is scheduled for future
              publishing.
            </p>

            {/* Search and Filter */}
            <div className="sc-controls-container">
              <div className="sc-search-wrapper">
                <img
                  src={Scheduled_Content_Search}
                  alt="Search"
                  className="sc-search-icon"
                />

                <input
                  type="text"
                  className="sc-search-input"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="sc-filter-wrapper">
                <select
                  className="sc-filter-dropdown"
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="Type">Type</option>
                  <option value="Video">Video</option>
                  <option value="Story">Story</option>
                  <option value="Reel">Reel</option>
                </select>

                <img
                  src={Dropdown_Icon}
                  alt="dropdown"
                  className="sc-dropdown-icon"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="sc-wrapper">
          {/* Table Section */}
          <div className="sc-table-wrapper">
            <table className="sc-table">
              <thead>
                <tr>
                  <th>Content</th>
                  <th>Type</th>
                  <th>Duration</th>
                  <th style={{ fontWeight: "700" }}>Scheduled Date / Time</th>
                </tr>
              </thead>
              <tbody>
                {scheduledContent.map((item) => (
                  <tr key={item?.id}>
                    <td>
                      <div className="sc-cell">
                        <div
                          className={`${item?.type === "Video" ? "" : "sc-img-container"}`}
                        >
                          <img
                            src={
                              item?.contentType === "Video"
                                ? item.generatedThumbnail
                                : item?.contentType === "Story"
                                  ? item?.thumbnailImage === ""
                                    ? item?.storyUrl
                                    : item?.thumbnailImage
                                  : item?.generatedThumbnail
                            }
                            alt={item?.title}
                            className={`${item?.contentTyep === "Video" ? "sc-thumbnail-video" : "sc-thumbnail-reel"}`}
                          />
                        </div>
                        <div className="sc-content-info">
                          <span className="sc-content-name">
                            {item.title || item.caption || "-"}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`sc-type-badge ${getTypeColor(item?.contentType)}`}
                      >
                        <td>{item?.contentType}</td>
                      </span>
                    </td>
                    <td>{`${item?.contentType === "Video" ? formatDurationSeconds(item.videoDuration) : item?.contentType === "Story" ? formatDurationSeconds(item?.storyDuration) : formatDurationSeconds(item?.reelDuration)}`}</td>
                    <td>
                      <div className="sc-scheduled-date-wrapper">
                        <div className="sc-scheduled-time-info">
                          <span className="sc-category-name">
                            {getMonth(item?.scheduledAt)}
                            <span style={{paddingLeft: "5px"}}>{getDay(item?.scheduledAt)}</span>
                            <span style={{paddingLeft: "5px"}}>{getYear(item?.scheduledAt)}</span>
                          </span>
                          <span className="sc-time-value">
                            {getTime(item?.scheduledAt)}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Pagination */}
            <div className="sc-pagination-container">
              <span className="sc-pagination-text">
                Showing{" "}
                <span style={{ color: "#161616" }}>
                  {startIndex}-{endIndex}
                </span>{" "}
                from <span style={{ color: "#161616" }}>{totalItems}</span> data
              </span>
              <div className="sc-pagination-buttons">
                <img
                  src={PaginationLeft}
                  alt="Left Arrow"
                  style={{
                    width: "32px",
                    height: "32px",
                    cursor: currentPage > 1 ? "pointer" : "not-allowed",
                    opacity: currentPage > 1 ? 1 : 0.5,
                  }}
                  onClick={() =>
                    currentPage > 1 && setCurrentPage((prev) => prev - 1)
                  }
                />

                {Array.from({ length: totalPages }, (_, index) => (
                  <button
                    key={index + 1}
                    className={`sc-pagination-page-number ${
                      currentPage === index + 1 ? "active" : ""
                    }`}
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
                <img
                  src={PaginationRight}
                  alt="Right Arrow"
                  style={{
                    width: "32px",
                    height: "32px",
                    cursor:
                      currentPage < totalPages ? "pointer" : "not-allowed",
                    opacity: currentPage < totalPages ? 1 : 0.5,
                  }}
                  onClick={() =>
                    currentPage < totalPages &&
                    setCurrentPage((prev) => prev + 1)
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
